import React, { useEffect, useMemo, useRef, useState } from "react";
import HeaderReport from "../../../../components/header/report";
import Loading from "../../../../components/loading";
import { toast } from "react-toastify";

export default function InstagramPosts({ token, selectedPostInstagram, handleCloseModalFilter }) {
	const [posts, setPosts] = useState([]);
	const [nextUrl, setNextUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState(null);
	const seenIdsRef = useRef(new Set());

	// monta a URL inicial apenas quando o token muda
	const initialUrl = useMemo(() => {
		if (!token) return null;
		const fields =
			"id,caption,media_url,media_type,thumbnail_url,permalink,timestamp,children{media_url,media_type,thumbnail_url}";
		const limit = 15;
		return `https://graph.instagram.com/me/media?fields=${fields}&limit=${limit}&access_token=${token}`;
	}, [token]);

	// Thumb helper (igual ao seu, com pequenos ajustes)
	const getThumbnailUrl = (post) => {
		if (post.media_type === "VIDEO") {
			return post.thumbnail_url || "/fallback-video.png";
		}
		if (post.media_type === "CAROUSEL_ALBUM" && post.children?.data?.length) {
			const first = post.children.data[0];
			return first.thumbnail_url || first.media_url || "/fallback-image.png";
		}
		return post.thumbnail_url || post.media_url || "/fallback-image.png";
	};

	const fetchPage = async (url, { append } = { append: false }) => {
		if (!url || loading) return;
		setLoading(true);
		setErr(null);
		try {
			const res = await fetch(url);
			const data = await res.json();

			if (!res.ok) {
				throw new Error(data?.error?.message || "Erro ao buscar posts do Instagram");
			}

			const incoming = Array.isArray(data.data) ? data.data : [];

			// evita duplicados entre páginas
			const newOnes = [];
			for (const p of incoming) {
				if (!seenIdsRef.current.has(p.id)) {
					seenIdsRef.current.add(p.id);
					newOnes.push(p);
				}
			}

			setPosts((prev) => (append ? [...prev, ...newOnes] : newOnes));
			setNextUrl(data?.paging?.next ?? null); // guarda a próxima página (ou null se acabou)
		} catch (e) {
			toast.error("Não foi possível conectar com o instagram " + e.message);
			handleCloseModalFilter();
			setErr(e.message);
		} finally {
			setLoading(false);
		}
	};

	// primeira carga
	useEffect(() => {
		// reset ao trocar token
		seenIdsRef.current.clear();
		setPosts([]);
		setNextUrl(null);
		if (initialUrl) fetchPage(initialUrl, { append: false });
	}, [initialUrl]);

	const handleSelect = (post) => {
		selectedPostInstagram({
			id: post.id,
			imageUrl: getThumbnailUrl(post),
			caption: post.caption || "",
			permalink: post.permalink || "",
			mediaType: post.media_type,
			timestamp: post.timestamp,
		});
		handleCloseModalFilter();
	};

	return (
		<div className="max-w-6xl mx-auto p-4">
			{loading ? (
				<Loading mensagem={"Carregando..."} />
			) : (
				<>
					<div className="border-bottom border-gray-300 mb-4">
						<HeaderReport title={"Posts"} subTitle={"Selecione um post"} />
					</div>
					{err && <div className="mb-4 rounded-xl bg-red-100 text-red-800 p-3">{err}</div>}

					{/* Grid de posts */}
					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
						{posts.map((post) => (
							<div
								key={post.id}
								className="bg-white shadow-md rounded-2xl overflow-hidden hover:shadow-lg transition"
							>
								{/* Imagem ou vídeo (use a media_url para exibir; para selecionar, usamos a thumb via helper) */}
								{post.media_type === "IMAGE" || post.media_type === "CAROUSEL_ALBUM" ? (
									<img
										src={post.media_url}
										alt={post.caption || `Post ${post.id}`}
										className="w-full h-48 object-cover"
										loading="lazy"
									/>
								) : post.media_type === "VIDEO" ? (
									<video controls className="w-full h-48 object-cover">
										<source src={post.media_url} type="video/mp4" />
									</video>
								) : null}

								<div className="p-3 flex justify-center">
									<button
										onClick={() => handleSelect(post)}
										className="px-4 py-2 rounded-xl text-white font-semibold transition bg-blue-500 hover:bg-blue-600 disabled:opacity-60"
										disabled={loading}
									>
										Selecionar
									</button>
								</div>
							</div>
						))}
					</div>

					{/* Paginação */}
					<div className="flex justify-center mt-6">
						{nextUrl ? (
							<button
								onClick={() => fetchPage(nextUrl, { append: true })}
								className="px-6 py-2 bg-gray-800 text-white rounded-xl shadow-md hover:bg-gray-900 transition disabled:opacity-60"
								disabled={loading}
							>
								{loading ? "Carregando..." : "Carregar mais"}
							</button>
						) : (
							posts.length > 0 && <span className="text-sm text-gray-500">Não há mais posts.</span>
						)}
					</div>
				</>
			)}
		</div>
	);
}
